import { createAsyncThunk } from "@reduxjs/toolkit";
import { CreateSessionRequest } from "../contracts/CreateSessionRequest";
import { UpdateSessionRequest } from "../contracts/UpdateSessionRequest";
import { SessionResponse } from "../contracts/SessionResponse";
import { PaginationRequest } from "../contracts/PaginationRequest";
import { PaginatedResult } from "../contracts/PaginatedResult";
import { Session } from "../models/Session";
import { SessionResponseMapper } from "../mappers/SessionResponseMapper";
import { SessionRequestMapper } from "../mappers/SessionRequestMapper";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import axiosInstance from "../../axiosInstance/axiosInstance";

const responseMapper = new SessionResponseMapper();
const requestMapper = new SessionRequestMapper();

/**
 * Async thunk for fetching paginated sessions
 */
export const fetchSessionsPaginated = createAsyncThunk<
  PaginatedResult<Session>,
  PaginationRequest
>(
  "sessions/fetchSessionsPaginated",
  async (paginationRequest: PaginationRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<
        PaginatedResult<SessionResponse>
      >("/Booking", {
        params: {
          page: paginationRequest.page,
          pageSize: paginationRequest.pageSize,
        },
      });
      const paginatedResult = response.data;
      const sessions = paginatedResult.items.map((sessionResponse) =>
        responseMapper.toSession(sessionResponse)
      );
      return {
        ...paginatedResult,
        items: sessions,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while fetching sessions"
      );
    }
  }
);

/**
 * Async thunk for fetching sessions by date range with pagination
 */
export const fetchSessionsByDateRangePaginated = createAsyncThunk<
  PaginatedResult<Session>,
  { startDate: string; endDate: string; paginationRequest: PaginationRequest }
>(
  "sessions/fetchSessionsByDateRangePaginated",
  async ({ startDate, endDate, paginationRequest }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<
        PaginatedResult<SessionResponse>
      >("/Booking/date-range", {
        params: {
          startDate,
          endDate,
          page: paginationRequest.page,
          pageSize: paginationRequest.pageSize,
        },
      });

      const paginatedResult = response.data;
      const sessions = paginatedResult.items.map((sessionResponse) =>
        responseMapper.toSession(sessionResponse)
      );

      return {
        ...paginatedResult,
        items: sessions,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while fetching sessions by date range"
      );
    }
  }
);

/**
 * Async thunk for fetching a session by ID
 */
export const fetchSessionById = createAsyncThunk<
  Session,
  string,
  { rejectValue: ApiErrorResponse }
>("sessions/fetchSession", async (sessionId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<SessionResponse>(
      `/Booking/${sessionId}`
    );
    return responseMapper.toSession(response.data);
  } catch (error: any) {
    const apiError: ApiErrorResponse = {
      message:
        error.response?.data?.message ||
        "Network error occurred while fetching session",
      statusCode: error.response?.status,
      errors: error.response?.data?.errors,
    };

    // Handle 404 specifically for session not found
    if (error.response?.status === 404) {
      apiError.message = error.response?.data?.message || "Session not found";
    }

    return rejectWithValue(apiError);
  }
});

/**
 * Async thunk for creating a new session
 * If no session data is provided, it uses currentSession from state
 */
export const createSession = createAsyncThunk<Session, Session>(
  "sessions/createSession",
  async (sessionData: Session, { rejectWithValue }) => {
    try {
      const requestData: CreateSessionRequest =
        requestMapper.toCreateSessionRequest(sessionData);
      const response = await axiosInstance.post<SessionResponse>(
        "/Booking",
        requestData
      );
      return responseMapper.toSession(response.data);
    } catch (error: any) {
      const apiError: ApiErrorResponse = {
        message:
          error.response?.data?.message ||
          "Network error occurred while creating session",
        statusCode: error.response?.status,
        errors: error.response?.data?.errors,
      };
      return rejectWithValue(apiError);
    }
});

/**
 * Async thunk for updating an existing session
 * If no session data is provided, it uses currentSession from state
 */
export const updateSession = createAsyncThunk<
  Session,
  { id?: string; session?: Session },
  { state: any; rejectValue: ApiErrorResponse }
>("sessions/updateSession", async ({ id, session }, { rejectWithValue }) => {
  try {
    const sessionToUpdate = session;
    const sessionId = id;
    if (!sessionToUpdate) {
      return rejectWithValue({
        message: "No session data provided",
        statusCode: 400,
      });
    }

    if (!sessionId) {
      return rejectWithValue({
        message: "No session ID provided",
        statusCode: 400,
      });
    }
    const requestData: UpdateSessionRequest =
      requestMapper.toUpdateSessionRequest(sessionToUpdate);
    const response = await axiosInstance.put<SessionResponse>(
      `/Booking/${sessionId}`,
      requestData
    );
    return responseMapper.toSession(response.data);
  } catch (error: any) {
    const apiError: ApiErrorResponse = {
      message:
        error.response?.data?.message ||
        "Network error occurred while updating session",
      statusCode: error.response?.status,
      errors: error.response?.data?.errors,
    };
    return rejectWithValue(apiError);
  }
});

/**
 * Async thunk for adding an invitee to a session
 */
export const addInviteeToSession = createAsyncThunk<
  Session,
  { sessionId: string; invitedUserId: string; type: number }
>(
  "sessions/addInviteeToSession",
  async ({ sessionId, invitedUserId, type }, { rejectWithValue }) => {
    try {
      const requestData = {
        invitedUserId,
        typeRequest: type,
      };

      const response = await axiosInstance.post<SessionResponse>(
        `/Booking/${sessionId}/invitees`,
        requestData
      );
      return responseMapper.toSession(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while adding invitee to session"
      );
    }
  }
);

/**
 * Async thunk for removing an invitee from a session
 */
export const removeInviteeFromSession = createAsyncThunk<
  Session,
  { sessionId: string; inviteeId: string }
>(
  "sessions/removeInviteeFromSession",
  async ({ sessionId, inviteeId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete<SessionResponse>(
        `/Booking/${sessionId}/invitees/${inviteeId}`
      );
      return responseMapper.toSession(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while removing invitee from session"
      );
    }
  }
);

/**
 * Async thunk for fetching upcoming sessions
 * Uses the date range endpoint to efficiently fetch sessions from current date onwards
 */
export const fetchUpcomingSessions = createAsyncThunk<Session[], void>(
  "sessions/fetchUpcomingSessions",
  async (_, { rejectWithValue }) => {
    try {
      const now = new Date();
      const currentDateTime = now.toISOString();

      // Set end date to 1 year from now to get all upcoming sessions
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
      const endDateTime = endDate.toISOString();

      const response = await axiosInstance.get<
        PaginatedResult<SessionResponse>
      >("/Booking/date-range", {
        params: {
          startDate: currentDateTime,
          endDate: endDateTime,
          page: 1,
          pageSize: 50, // Fetch enough upcoming sessions
        },
      });

      const paginatedResult = response.data;
      const sessions = paginatedResult.items.map((sessionResponse) =>
        responseMapper.toSession(sessionResponse)
      );

      // Sort by dateStart ascending (should already be sorted by backend, but ensure it)
      sessions.sort(
        (a, b) =>
          new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
      );

      return sessions;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while fetching upcoming sessions"
      );
    }
  }
);

/**
 * Async thunk for fetching recently completed sessions
 * Uses the date range endpoint to efficiently fetch sessions from past dates
 */
export const fetchRecentlyCompletedSessions = createAsyncThunk<Session[], void>(
  "sessions/fetchRecentlyCompletedSessions",
  async (_, { rejectWithValue }) => {
    try {
      const now = new Date();
      const currentDateTime = now.toISOString();

      // Set start date to 1 month ago to get recent completed sessions
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      const startDateTime = startDate.toISOString();

      const response = await axiosInstance.get<
        PaginatedResult<SessionResponse>
      >("/Booking/date-range", {
        params: {
          startDate: startDateTime,
          endDate: currentDateTime,
          page: 1,
          pageSize: 50, // Fetch enough recent completed sessions
        },
      });

      const paginatedResult = response.data;
      const sessions = paginatedResult.items.map((sessionResponse) =>
        responseMapper.toSession(sessionResponse)
      );

      // Filter only completed sessions (sessions that have ended)
      const completedSessions = sessions.filter((session) => {
        const sessionEndTime = new Date(session.dateStart);
        sessionEndTime.setMinutes(sessionEndTime.getMinutes() + session.duration);
        return sessionEndTime < now;
      });

      // Sort by dateStart descending (most recent first for completed sessions)
      completedSessions.sort(
        (a, b) =>
          new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime()
      );

      return completedSessions;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Network error occurred while fetching recently completed sessions"
      );
    }
  }
);
